/**
 * プリセット作成/編集フォーム（Presentation）
 */
import { Sparkles } from "lucide-react";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SkillAutocomplete } from "../../../components/SkillAutocomplete";
import type {
  CreatePresetInput,
  ParseTextResult,
  Preset,
} from "../../../hooks/use-preset-api";

type PresetFormPageProps = {
  isEdit: boolean;
  initialData?: Preset | null;
  loading: boolean;
  onSubmit: (input: CreatePresetInput) => Promise<void>;
  onParseText: (text: string, presetType: string) => Promise<ParseTextResult>;
  isSubmitting: boolean;
  isParsing: boolean;
};

type FormData = {
  name: string;
  presetType: string;
  sourceText: string;
  skills: string[];
  reward: string;
  station: string;
  isRemote: string;
  remoteFrequency: string;
  availableFrom: string;
  industryCategory: string;
  note: string;
  // HR only
  age: string;
  hrTypes: string;
  isJapanese: string;
  // Matter only
  freelanceEnabled: string;
  ageLimitation: string;
  otherCountriesEnabled: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

/**
 * プリセット作成/編集フォーム
 * @param {PresetFormPageProps} props コンポーネントProps
 * @return {JSX.Element} フォームページ
 */
export const PresetFormPage: FC<PresetFormPageProps> = ({
  isEdit,
  initialData,
  loading,
  onSubmit,
  onParseText,
  isSubmitting,
  isParsing,
}) => {
  const [formData, setFormData] = useState<FormData>(() => ({
    name: initialData?.name ?? "",
    presetType: initialData?.presetType ?? "hr",
    sourceText: initialData?.sourceText ?? "",
    skills: initialData?.skills ?? [],
    reward: initialData?.reward != null ? String(initialData.reward) : "",
    station: initialData?.station ?? "",
    isRemote: initialData?.isRemote != null ? String(initialData.isRemote) : "",
    remoteFrequency: initialData?.remoteFrequency ?? "",
    availableFrom: initialData?.availableFrom ?? "",
    industryCategory: initialData?.industryCategory ?? "",
    note: initialData?.note ?? "",
    age: initialData?.age != null ? String(initialData.age) : "",
    hrTypes: initialData?.hrTypes ?? "",
    isJapanese:
      initialData?.isJapanese != null ? String(initialData.isJapanese) : "",
    freelanceEnabled:
      initialData?.freelanceEnabled != null
        ? String(initialData.freelanceEnabled)
        : "",
    ageLimitation:
      initialData?.ageLimitation != null
        ? String(initialData.ageLimitation)
        : "",
    otherCountriesEnabled:
      initialData?.otherCountriesEnabled != null
        ? String(initialData.otherCountriesEnabled)
        : "",
  }));

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        presetType: initialData.presetType,
        sourceText: initialData.sourceText ?? "",
        skills: initialData.skills ?? [],
        reward: initialData.reward != null ? String(initialData.reward) : "",
        station: initialData.station ?? "",
        isRemote:
          initialData.isRemote != null ? String(initialData.isRemote) : "",
        remoteFrequency: initialData.remoteFrequency ?? "",
        availableFrom: initialData.availableFrom ?? "",
        industryCategory: initialData.industryCategory ?? "",
        note: initialData.note ?? "",
        age: initialData.age != null ? String(initialData.age) : "",
        hrTypes: initialData.hrTypes ?? "",
        isJapanese:
          initialData.isJapanese != null ? String(initialData.isJapanese) : "",
        freelanceEnabled:
          initialData.freelanceEnabled != null
            ? String(initialData.freelanceEnabled)
            : "",
        ageLimitation:
          initialData.ageLimitation != null
            ? String(initialData.ageLimitation)
            : "",
        otherCountriesEnabled:
          initialData.otherCountriesEnabled != null
            ? String(initialData.otherCountriesEnabled)
            : "",
      });
    }
  }, [initialData]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "プリセット名は必須です";
    }
    if (formData.reward && Number.isNaN(Number(formData.reward))) {
      newErrors.reward = "数値を入力してください";
    }
    if (formData.age && Number.isNaN(Number(formData.age))) {
      newErrors.age = "数値を入力してください";
    }
    if (
      formData.ageLimitation &&
      Number.isNaN(Number(formData.ageLimitation))
    ) {
      newErrors.ageLimitation = "数値を入力してください";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitError(null);
    try {
      const toBool = (v: string) =>
        v === "true" ? true : v === "false" ? false : null;
      const toNum = (v: string) => (v ? Number(v) : null);

      await onSubmit({
        name: formData.name.trim(),
        presetType: formData.presetType,
        sourceText: formData.sourceText || null,
        skills: formData.skills.length > 0 ? formData.skills : null,
        reward: toNum(formData.reward),
        station: formData.station || null,
        isRemote: toBool(formData.isRemote),
        remoteFrequency: formData.remoteFrequency || null,
        availableFrom: formData.availableFrom || null,
        industryCategory: formData.industryCategory || null,
        note: formData.note || null,
        age: toNum(formData.age),
        hrTypes: formData.hrTypes || null,
        isJapanese: toBool(formData.isJapanese),
        freelanceEnabled: toBool(formData.freelanceEnabled),
        ageLimitation: toNum(formData.ageLimitation),
        otherCountriesEnabled: toBool(formData.otherCountriesEnabled),
      });
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "保存に失敗しました");
    }
  };

  const handleChange = (field: keyof FormData, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleAddSkill = (skill: string) => {
    if (skill && !formData.skills.includes(skill)) {
      handleChange("skills", [...formData.skills, skill]);
    }
  };

  const handleRemoveSkill = (skill: string) => {
    handleChange(
      "skills",
      formData.skills.filter(s => s !== skill),
    );
  };

  const handleParseText = async () => {
    if (!formData.sourceText.trim()) return;
    try {
      const result = await onParseText(
        formData.sourceText,
        formData.presetType,
      );
      setFormData(prev => ({
        ...prev,
        name: result.name ?? prev.name,
        presetType: isEdit
          ? prev.presetType
          : (result.presetType ?? prev.presetType),
        skills: result.skills ?? prev.skills,
        reward: result.reward != null ? String(result.reward) : prev.reward,
        station: result.station ?? prev.station,
        isRemote:
          result.isRemote != null ? String(result.isRemote) : prev.isRemote,
        remoteFrequency: result.remoteFrequency ?? prev.remoteFrequency,
        availableFrom: result.availableFrom ?? prev.availableFrom,
        industryCategory: result.industryCategory ?? prev.industryCategory,
        age: result.age != null ? String(result.age) : prev.age,
        hrTypes: result.hrTypes ?? prev.hrTypes,
        isJapanese:
          result.isJapanese != null
            ? String(result.isJapanese)
            : prev.isJapanese,
        freelanceEnabled:
          result.freelanceEnabled != null
            ? String(result.freelanceEnabled)
            : prev.freelanceEnabled,
        ageLimitation:
          result.ageLimitation != null
            ? String(result.ageLimitation)
            : prev.ageLimitation,
        otherCountriesEnabled:
          result.otherCountriesEnabled != null
            ? String(result.otherCountriesEnabled)
            : prev.otherCountriesEnabled,
      }));
    } catch {
      setSubmitError("AI解析に失敗しました。手動で入力してください。");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  const isHr = formData.presetType === "hr";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          {isEdit ? "プリセットを編集" : "プリセットを作成"}
        </h1>
        <p className="text-base-content/60 mt-1">
          マッチングに使用する人材/案件プロファイルを設定します
        </p>
      </div>

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            {submitError && (
              <div className="alert alert-error">
                <span>{submitError}</span>
              </div>
            )}

            {/* AI テキスト解析（最上部） */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">
                  フリーテキスト入力
                </span>
                <button
                  type="button"
                  className="btn btn-primary btn-sm gap-1"
                  onClick={handleParseText}
                  disabled={isParsing || !formData.sourceText.trim()}
                >
                  {isParsing ? (
                    <span className="loading loading-spinner loading-xs" />
                  ) : (
                    <Sparkles className="w-3.5 h-3.5" />
                  )}
                  AIで自動入力
                </button>
              </label>
              <textarea
                value={formData.sourceText}
                onChange={e => handleChange("sourceText", e.target.value)}
                className="textarea textarea-bordered"
                rows={6}
                placeholder={
                  "人材情報や案件情報のテキストを貼り付けて「AIで自動入力」を押してください。\nプリセット名・種別（人材/案件）・スキル・単価などが自動で入力されます。"
                }
              />
              <label className="label">
                <span className="label-text-alt text-base-content/50">
                  テキストを貼り付けるだけで、以下のフィールドがAIにより自動入力されます
                </span>
              </label>
            </div>

            <div className="divider my-2">基本情報</div>

            {/* プリセット名・種別 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    プリセット名 <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => handleChange("name", e.target.value)}
                  className={`input input-bordered ${errors.name ? "input-error" : ""}`}
                  placeholder="AIが自動入力、または手動で入力"
                />
                {errors.name && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.name}
                    </span>
                  </label>
                )}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    種別 <span className="text-error">*</span>
                  </span>
                </label>
                <select
                  value={formData.presetType}
                  onChange={e => handleChange("presetType", e.target.value)}
                  className="select select-bordered"
                  disabled={isEdit}
                >
                  <option value="hr">人材</option>
                  <option value="matter">案件</option>
                </select>
              </div>
            </div>

            <div className="divider my-2">詳細フィールド</div>

            {/* スキル */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">スキル</span>
              </label>
              <SkillAutocomplete
                selectedSkills={formData.skills}
                onAddSkill={handleAddSkill}
                onRemoveSkill={handleRemoveSkill}
                placeholder="スキルを入力（候補から選択 or Enter で追加）"
              />
            </div>

            {/* 単価・勤務地 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    {isHr ? "希望単価" : "予算単価"}（円）
                  </span>
                </label>
                <input
                  type="number"
                  value={formData.reward}
                  onChange={e => handleChange("reward", e.target.value)}
                  className={`input input-bordered ${errors.reward ? "input-error" : ""}`}
                  placeholder="600000"
                  min={0}
                  step={10000}
                />
                {errors.reward && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.reward}
                    </span>
                  </label>
                )}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">勤務地/最寄駅</span>
                </label>
                <input
                  type="text"
                  value={formData.station}
                  onChange={e => handleChange("station", e.target.value)}
                  className="input input-bordered"
                  placeholder="渋谷"
                />
              </div>
            </div>

            {/* リモート */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    {isHr ? "リモート希望" : "リモート可"}
                  </span>
                </label>
                <select
                  value={formData.isRemote}
                  onChange={e => handleChange("isRemote", e.target.value)}
                  className="select select-bordered"
                >
                  <option value="">未指定</option>
                  <option value="true">はい</option>
                  <option value="false">いいえ</option>
                </select>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">リモート頻度</span>
                </label>
                <select
                  value={formData.remoteFrequency}
                  onChange={e =>
                    handleChange("remoteFrequency", e.target.value)
                  }
                  className="select select-bordered"
                >
                  <option value="">未指定</option>
                  <option value="full_remote">フルリモート</option>
                  <option value="hybrid">ハイブリッド</option>
                  <option value="onsite_only">オンサイトのみ</option>
                </select>
              </div>
            </div>

            {/* 稼働開始・業界 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    {isHr ? "稼働開始可能時期" : "案件開始時期"}
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.availableFrom}
                  onChange={e => handleChange("availableFrom", e.target.value)}
                  className="input input-bordered"
                  placeholder="即日、2026年3月〜"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">業界カテゴリ</span>
                </label>
                <input
                  type="text"
                  value={formData.industryCategory}
                  onChange={e =>
                    handleChange("industryCategory", e.target.value)
                  }
                  className="input input-bordered"
                  placeholder="金融、EC"
                />
              </div>
            </div>

            {/* HR専用フィールド */}
            {isHr && (
              <>
                <div className="divider my-2">人材専用</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">年齢</span>
                    </label>
                    <input
                      type="number"
                      value={formData.age}
                      onChange={e => handleChange("age", e.target.value)}
                      className={`input input-bordered ${errors.age ? "input-error" : ""}`}
                      min={18}
                      max={99}
                    />
                  </div>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">雇用形態</span>
                    </label>
                    <select
                      value={formData.hrTypes}
                      onChange={e => handleChange("hrTypes", e.target.value)}
                      className="select select-bordered"
                    >
                      <option value="">未指定</option>
                      <option value="fullTimeEmployee">正社員</option>
                      <option value="contractEmployee">契約社員</option>
                      <option value="freelancer">フリーランス</option>
                    </select>
                  </div>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">日本国籍</span>
                    </label>
                    <select
                      value={formData.isJapanese}
                      onChange={e => handleChange("isJapanese", e.target.value)}
                      className="select select-bordered"
                    >
                      <option value="">未指定</option>
                      <option value="true">はい</option>
                      <option value="false">いいえ</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* Matter専用フィールド */}
            {!isHr && (
              <>
                <div className="divider my-2">案件専用</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">フリーランス可</span>
                    </label>
                    <select
                      value={formData.freelanceEnabled}
                      onChange={e =>
                        handleChange("freelanceEnabled", e.target.value)
                      }
                      className="select select-bordered"
                    >
                      <option value="">未指定</option>
                      <option value="true">可</option>
                      <option value="false">不可</option>
                    </select>
                  </div>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">年齢制限</span>
                    </label>
                    <input
                      type="number"
                      value={formData.ageLimitation}
                      onChange={e =>
                        handleChange("ageLimitation", e.target.value)
                      }
                      className={`input input-bordered ${errors.ageLimitation ? "input-error" : ""}`}
                      min={18}
                      max={99}
                    />
                  </div>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">外国籍可</span>
                    </label>
                    <select
                      value={formData.otherCountriesEnabled}
                      onChange={e =>
                        handleChange("otherCountriesEnabled", e.target.value)
                      }
                      className="select select-bordered"
                    >
                      <option value="">未指定</option>
                      <option value="true">可</option>
                      <option value="false">不可</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* メモ */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">メモ/備考</span>
              </label>
              <textarea
                value={formData.note}
                onChange={e => handleChange("note", e.target.value)}
                className="textarea textarea-bordered"
                rows={2}
                placeholder="その他の条件やメモ"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Link to="/settings/presets" className="btn btn-ghost">
                キャンセル
              </Link>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : isEdit ? (
                  "更新"
                ) : (
                  "作成"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
